import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { UploaderAcceptFormat } from "../../../../shared/components/file-uploader/file-uploader.component";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { ImageEntity } from "../../../../shared/entities/file/image.entity";
import { ByteUnit } from "../../../../shared/functions/byte-unit";
import { AppDataPostTags } from "../../../../shared/models/appData";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import {
  CampaignPostContentType,
  PostPlatform,
} from "../../../../shared/enums/campaignPost.enums";
import { MAX_INSTAGRAM_VIDEO_DURATION } from "../../../../shared/models/campaignPost";
import { AppDataService } from "../../../../shared/services/api/app-data.service";
import { cloneWith } from "../../../../shared/services/clonable";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { BrandCampaignContentFormPostSuggestedTextComponent } from "../brand-campaign-content-form-post-text/brand-campaign-content-form-post-suggested-text.component";
import { AnyToImageEntityMapper } from "../../../../shared/mappers/image.entity.mapper";
import { AnyToVideoEntityMapper } from "../../../../shared/mappers/video.entity.mapper";

export interface CampaignContentFormPostVideoDialogData
  extends BrandCampaignAddContentDialogData {
  readonly post?: BrandCampaignFacebookPost;
}

export type CampaignContentFormPostVideoDialogResult = undefined;

interface ContentUploadData {
  readonly media_type: CampaignPostContentType;
  readonly platform: PostPlatform;
  post_id?: string;
}

@Component({
  selector: "app-campaign-content-form-post-video-dialog",
  templateUrl: "./campaign-content-form-post-video-dialog.component.html",
  styleUrls: ["./campaign-content-form-post-video-dialog.component.scss"],
})
export class CampaignContentFormPostVideoDialogComponent implements OnInit {
  public saving = false;
  public campaign: BrandCampaign;
  public campaignDetails: BrandCampaignDetails;
  public category: BrandCampaignCategory;

  public readonly MAX_IMAGE_UPLOAD_SIZE_IN_BYTES = ByteUnit.convert(
    8,
    ByteUnit.MB,
  );
  public readonly MAX_VIDEO_DURATION_MINUTES = 15;
  public post: BrandCampaignFacebookPost;
  public postTags?: AppDataPostTags[];

  public isNewPost = false;
  public maxVideoFileSizeInBytes?: number;
  public isPostToInstagramDisabled = false;

  public imageUploaderSubtitle = "";
  public uploaderUrl: string;

  public readonly uploaderFieldName = "file";
  public readonly acceptImageFormat = UploaderAcceptFormat.Image;
  public readonly acceptVideoFormat = UploaderAcceptFormat.VideoMP4;

  public videoUploaderSubtitle = "";
  public uploadData: ContentUploadData;
  public isSaveDisabled = false;

  public imagefileUploaderResponseMapper = AnyToImageEntityMapper;
  public videofileUploaderResponseMapper = AnyToVideoEntityMapper;

  @ViewChild("suggestedText")
  public suggestedText?: BrandCampaignContentFormPostSuggestedTextComponent;

  constructor(
    private readonly translateService: TranslateService,
    private readonly appDataService: AppDataService,
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly notificationService: NotificationService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormPostVideoDialogComponent,
      CampaignContentFormPostVideoDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormPostVideoDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;

    if (data.post) {
      this.post = cloneWith(data.post);
      if (this.post.videoDuration > MAX_INSTAGRAM_VIDEO_DURATION) {
        this.isPostToInstagramDisabled = true;
        this.post.postToInstagram = false;
      }
    } else {
      this.isNewPost = true;
      this.isSaveDisabled = true;
      this.post = BrandCampaignFacebookPost.create(
        true,
        CampaignPostContentType.Video,
      );
    }

    this.uploaderUrl =
      this.brandCampaignPostService.getAddCampaignDetailCategoryPostFileUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      );

    this.uploadData = {
      media_type: data.post?.mediaType as CampaignPostContentType,
      platform: PostPlatform.Facebook,
    };

    if (this.post.id) {
      this.uploadData.post_id = this.post.id.toString();
    }
  }

  public ngOnInit(): void {
    this.appDataService.get().then((appData) => {
      this.postTags = appData.postTags ?? [];
      this.maxVideoFileSizeInBytes = ByteUnit.convert(
        appData.maxUploadFileSize,
        ByteUnit.MB,
      );

      this.imageUploaderSubtitle =
        this.translateService.instant(
          "brand.campaign.contentCalendarVideoPost.validImageExtensions",
        ) +
        " jpg, jpeg, png. " +
        ByteUnit.convert(
          this.MAX_IMAGE_UPLOAD_SIZE_IN_BYTES,
          ByteUnit.Byte,
          ByteUnit.MB,
        ) +
        " MB";

      this.videoUploaderSubtitle =
        this.translateService.instant(
          "brand.campaign.contentCalendarVideoPost.validFileExtension",
        ) +
        " mp4. Max " +
        ByteUnit.convert(
          this.maxVideoFileSizeInBytes,
          ByteUnit.Byte,
          ByteUnit.MB,
        ) +
        " MB. " +
        this.translateService.instant(
          "brand.campaign.contentCalendarVideoPost.thumbnailAuto",
        ) +
        " Max 4 MB. Bitrate Max 25 Mbps. Sample Rate Max 48 khz";

      this.postTags = appData.postTags ?? [];
    });
  }

  public onSaveContent(): void {
    this.saving = true;
    if (this.isNewPost) {
      this.createPost();
    } else {
      this.savePost();
    }
  }

  private createPost(): void {
    this.brandCampaignPostService
      .createCampaignDetailCategoryPost(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.post,
      )
      .subscribe({
        next: (post) => {
          this.post = post;
          this.category.posts.push(this.post);
          this.dialogRef.close();
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  public savePost(): void {
    this.brandCampaignPostService
      .updateCampaignDetailCategoryPost(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.post,
      )
      .subscribe({
        next: (post) => {
          this.post = post;
          this.category.posts = this.category.posts.map((catPost) =>
            catPost.id === post.id ? post : catPost,
          );
          this.dialogRef.close();
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  public onTextChanged(text: string): void {
    this.post.text = text;
    this.updateSaveDisabled();
  }

  public handleImageUploadResponse(file: ImageEntity): void {
    this.post.imageFileId = file.id;
    this.post.image = file.url;
    this.updateSaveDisabled();
    this.notificationService.success("shared.imageUploadedSuccessfully");
  }

  public handleVideoUploadResponse(file: ImageEntity): void {
    this.post.videoFileId = file.id;
    this.post.videoUrl = file.videoUrl;
    if (file.videoDuration > MAX_INSTAGRAM_VIDEO_DURATION) {
      this.isPostToInstagramDisabled = true;
      this.post.postToInstagram = false;
    } else {
      this.isPostToInstagramDisabled = false;
    }
    this.updateSaveDisabled();
    this.notificationService.success("shared.videoUploadedSuccessfully");
  }

  public updateSaveDisabled(): void {
    const isPostIncomplete =
      (!this.post.postToFacebook && !this.post.postToInstagram) ||
      !this.post.image ||
      !this.post.videoUrl ||
      !this.post.text;

    this.isSaveDisabled =
      this.saving || isPostIncomplete || !this.suggestedText?.isValid;
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }
}
